import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {endOfMonth, format, parseISO, startOfMonth} from "date-fns";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {bulkDownloadInvoicesThunk, deleteInvoice, fetchInvoices, invoicesSelectors} from "../store/invoicesSlice";
import {fetchTenants, tenantsSelectors} from "../store/tenantsSlice";
import {type Invoice, isPlanInvoice, isSmsInvoice} from "../types/invoice";
import {getInvoiceById} from "../api/invoicesApi";
import {escapeForCsv} from "../util/csv";
import {escapeHtml, openPrintWindowSync, renderPrintPageInto} from "../util/printUtils";
import {createdWithinDays, invoiceTotalInCents} from "../utils/invoiceList";
import {formatDate as formatGermanDate, formatEuroCents} from "../util/format";
import {PageHeader} from "../components/ui/PageHeader";
import {MonoText, StatusChip} from "../components/ui/StatusChip";
import {colors} from "../theme/tokens";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import PrintIcon from "@mui/icons-material/Print";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

type SortColumn = "tenant" | "invoiceNumber" | "createdAt";
type SortDirection = "asc" | "desc";

export type InvoiceWithTenantName = Invoice & {tenantName: string};

function formatDateTime(iso: string): string {
    try {
        return format(parseISO(iso), "dd.MM.yy HH:mm");
    } catch {
        return iso;
    }
}

function formatDayMonth(iso: string): string {
    try {
        return format(parseISO(iso), "dd.MM.");
    } catch {
        return iso;
    }
}

function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export function InvoicesPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const invoices = useAppSelector(invoicesSelectors.selectAll);
    const tenants = useAppSelector(tenantsSelectors.selectAll);
    const status = useAppSelector((s) => s.invoices.status);
    const tenantsStatus = useAppSelector((s) => s.tenants.status);
    const error = useAppSelector((s) => s.invoices.error);
    const bulkDownloadStatus = useAppSelector((s) => s.invoices.bulkDownloadStatus);

    const [search, setSearch] = useState("");
    const [sortColumn, setSortColumn] = useState<SortColumn>("createdAt");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [dateFrom, setDateFrom] = useState<string>(() =>
            format(startOfMonth(new Date()), "yyyy-MM-dd"),
    );
    const [dateTo, setDateTo] = useState<string>(() =>
            format(endOfMonth(new Date()), "yyyy-MM-dd"),
    );
    const [snackMessage, setSnackMessage] = useState<string | null>(null);
    const [snackSeverity, setSnackSeverity] = useState<"success" | "error">("success");
    const [deleteTarget, setDeleteTarget] = useState<InvoiceWithTenantName | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (status !== "idle") return;
        void dispatch(fetchInvoices());
    }, [dispatch, status]);

    useEffect(() => {
        if (tenantsStatus !== "idle") return;
        void dispatch(fetchTenants());
    }, [dispatch, tenantsStatus]);

    const tenantsById = useMemo(() => new Map(tenants.map((t) => [t.id, t])), [tenants]);

    const inRange = useMemo(
            () => invoices.filter((inv) => !(inv as {deletedAt?: string | null}).deletedAt && createdWithinDays(inv.createdAt, dateFrom, dateTo)),
            [invoices, dateFrom, dateTo],
    );

    const filteredAndSorted = useMemo(() => {
        let list: InvoiceWithTenantName[] = inRange.map((inv) => ({
            ...inv,
            tenantName: tenantsById.get(inv.tenantId)?.name ?? t("INVOICES_UNKNOWN_TENANT"),
        }));

        const q = search.trim().toLowerCase();
        if (q) {
            list = list.filter(
                    (inv) =>
                            inv.tenantName.toLowerCase().includes(q) ||
                            inv.invoiceNumber.toLowerCase().includes(q) ||
                            inv.tenantId.toLowerCase().includes(q) ||
                            inv.billingAddress.companyName.toLowerCase().includes(q),
            );
        }

        list = [...list].sort((a, b) => {
            let cmp = 0;
            switch (sortColumn) {
                case "tenant":
                    cmp = a.tenantName.localeCompare(b.tenantName);
                    break;
                case "invoiceNumber":
                    cmp = a.invoiceNumber.localeCompare(b.invoiceNumber);
                    break;
                case "createdAt":
                    cmp =
                            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                    break;
                default:
                    return 0;
            }
            return sortDirection === "asc" ? cmp : -cmp;
        });
        return list;
    }, [inRange, tenantsById, search, sortColumn, sortDirection, t]);

    const visibleTotal = filteredAndSorted.reduce((sum, inv) => sum + invoiceTotalInCents(inv), 0);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const handleDownload = async (invoice: Invoice) => {
        try {
            const details = await getInvoiceById(invoice.id);
            if (details.presignedDownloadUrl) {
                window.open(details.presignedDownloadUrl, "_blank");
            } else {
                setSnackMessage(t("INVOICES_DOWNLOAD_UNAVAILABLE"));
                setSnackSeverity("error");
            }
        } catch {
            setSnackMessage(t("INVOICES_DOWNLOAD_UNAVAILABLE"));
            setSnackSeverity("error");
        }
    };

    const handleDeleteClick = (invoice: InvoiceWithTenantName) => setDeleteTarget(invoice);

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await dispatch(deleteInvoice(deleteTarget.id)).unwrap();
            setSnackMessage(t("INVOICES_DELETED"));
            setSnackSeverity("success");
            setDeleteTarget(null);
        } catch (err) {
            setSnackMessage(err instanceof Error ? err.message : t("INVOICES_DELETE_FAILED"));
            setSnackSeverity("error");
        } finally {
            setDeleting(false);
        }
    };

    const handleExportCsv = () => {
        const ROW_DELIMITER = "\r\n";
        const header =
                "Invoice number;Invoice ID;Tenant;Tenant ID;Total price;VAT (included);Invoice date;Stripe reference;Company;VAT ID;Last name;First name;Email;Address;City;Zip;Country" +
                ROW_DELIMITER;
        const rows = filteredAndSorted.map((inv) => {
            const od = inv.orderDetails as {totalPriceInCents?: number; vatTaxAmountInCents?: number};
            return [
                escapeForCsv(inv.invoiceNumber),
                escapeForCsv(inv.id),
                escapeForCsv(inv.tenantName),
                escapeForCsv(inv.tenantId),
                (od?.totalPriceInCents ?? 0) / 100,
                (od?.vatTaxAmountInCents ?? 0) / 100,
                format(parseISO(inv.createdAt), "dd.MM.yyyy"),
                escapeForCsv(inv.status.stripeSessionId),
                escapeForCsv(inv.billingAddress.companyName),
                escapeForCsv(inv.billingAddress.vatId),
                escapeForCsv(inv.billingAddress.lastName),
                escapeForCsv(inv.billingAddress.firstName),
                escapeForCsv(inv.billingAddress.billingEmail),
                escapeForCsv(inv.billingAddress.address),
                escapeForCsv(inv.billingAddress.city),
                escapeForCsv(inv.billingAddress.zipCode),
                escapeForCsv(inv.billingAddress.country.name),
            ].join(";");
        });
        const csv = header + rows.join(ROW_DELIMITER) + ROW_DELIMITER;
        const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
        downloadBlob(blob, `invoices-${dateFrom}-${dateTo}.csv`);
    };

    const handleExportZip = async () => {
        try {
            const result = await dispatch(
                    bulkDownloadInvoicesThunk({
                        dateFrom,
                        dateTo,
                        sortColumn,
                        sortDirection,
                    }),
            ).unwrap();
            if (result.presignedDownloadUrl) {
                window.open(result.presignedDownloadUrl, "_blank");
            }
        } catch {
            setSnackMessage(t("INVOICES_BULK_DOWNLOAD_FAILED"));
            setSnackSeverity("error");
        }
    };

    const handlePrint = () => {
        const printWindow = openPrintWindowSync();
        const intervalLabel = `${format(parseISO(dateFrom), "dd.MM.yyyy")} - ${format(parseISO(dateTo), "dd.MM.yyyy")}`;
        const rowsHtml = filteredAndSorted.map((inv) => {
            if (isPlanInvoice(inv)) {
                const od = inv.orderDetails;
                const plan = inv.product.plan;
                return `
          <div style="display:flex; margin: 8px 0;">
            <div style="width: 16%;">${formatDateTime(inv.createdAt)}</div>
            <div style="width: 66%;">
              <p>${escapeHtml(inv.invoiceNumber)} ${escapeHtml(inv.tenantName)}</p>
              <p>${escapeHtml(plan?.name ?? "")} ${od.durationInMonths} ${t("INVOICES_MONTHS")} ${od.numberOfEmployees} ${t("INVOICES_EMPLOYEES")}</p>
              <p>${t("INVOICES_VAT_INCLUDED", {percent: od.vatTaxPercentage})}</p>
            </div>
            <div style="width: 18%; text-align: right;">${((od.totalPriceInCents ?? 0) / 100).toFixed(2)} EUR</div>
          </div>`;
            }
            if (isSmsInvoice(inv)) {
                const od = inv.orderDetails;
                return `
          <div style="display:flex; margin: 8px 0;">
            <div style="width: 16%;">${formatDateTime(inv.createdAt)}</div>
            <div style="width: 66%;">
              <p>${escapeHtml(inv.invoiceNumber)} ${escapeHtml(inv.tenantName)}</p>
              <p>${od.numberOfSMS} ${t("INVOICES_SMS")}</p>
              <p>${t("INVOICES_VAT_INCLUDED", {percent: od.vatTaxPercentage})}</p>
            </div>
            <div style="width: 18%; text-align: right;">${((od.totalPriceInCents ?? 0) / 100).toFixed(2)} EUR</div>
          </div>`;
            }
            return "";
        }).join("");
        const bodyHtml = `
      <div style="padding: 16px;">
        <h3 style="margin-bottom: 16px;">${t("INVOICES_TITLE")} ${intervalLabel}</h3>
        ${rowsHtml}
      </div>`;
        renderPrintPageInto(printWindow, {
            title: `${t("INVOICES_TITLE")} ${intervalLabel}`,
            bodyHtml,
            extraCss: ".container { font-size: 11px; } p { margin-bottom: 0; }",
            showPrintActions: true,
            autoPrint: true,
        });
    };

    const sortLabels: Record<SortColumn, string> = {
        tenant: t("INVOICES_COLUMN_TENANT"),
        invoiceNumber: t("INVOICES_COLUMN_INVOICE_NUMBER"),
        createdAt: t("TENANTS_COLUMN_CREATED"),
    };

    const sortHeader = (column: SortColumn) => (
            <TableCell sortDirection={sortColumn === column ? sortDirection : false}>
                <TableSortLabel
                        active={sortColumn === column}
                        direction={sortColumn === column ? sortDirection : "asc"}
                        onClick={() => handleSort(column)}
                >
                    {sortLabels[column]}
                </TableSortLabel>
            </TableCell>
    );

    const scopeHint = (text: string, emphasized?: boolean) => (
            <Box component="span" sx={{ml: 1, fontSize: "11px", fontWeight: 400, color: emphasized ? colors.warningText : colors.faint}}>
                {text}
            </Box>
    );

    return (
            <>
                <PageHeader
                        title={t("INVOICES_TITLE")}
                        subtitle={t("INVOICES_SUBTITLE", {count: filteredAndSorted.length, total: formatEuroCents(visibleTotal)})}
                        actions={
                            <>
                                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
                                    {t("INVOICES_EXPORT_CSV")}
                                    {scopeHint(t("INVOICES_SCOPE_FILTERED"))}
                                </Button>
                                <Button
                                        variant="outlined"
                                        startIcon={<FolderZipIcon />}
                                        onClick={() => void handleExportZip()}
                                        disabled={bulkDownloadStatus === "loading" || inRange.length === 0}
                                        title={t("INVOICES_ZIP_SCOPE_EXPLANATION")}
                                >
                                    {bulkDownloadStatus === "loading" ? t("INVOICES_PREPARING") : t("INVOICES_EXPORT_ZIP")}
                                    {scopeHint(t("INVOICES_SCOPE_WHOLE_RANGE"), true)}
                                </Button>
                                <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
                                    {t("INVOICES_PRINT")}
                                    {scopeHint(t("INVOICES_SCOPE_FILTERED"))}
                                </Button>
                            </>
                        }
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5}}>
                    <TextField
                            placeholder={t("INVOICES_SEARCH_PLACEHOLDER")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            sx={{width: 320}}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{fontSize: 18, color: colors.faint}} />
                                            </InputAdornment>
                                    ),
                                },
                                htmlInput: {"aria-label": t("INVOICES_SEARCH_PLACEHOLDER")},
                            }}
                    />
                    <TextField
                            label={t("INVOICES_DATE_FROM")}
                            type="date"
                            size="small"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            sx={{width: 170}}
                            slotProps={{htmlInput: {"aria-label": t("INVOICES_DATE_FROM")}}}
                    />
                    <TextField
                            label={t("INVOICES_DATE_TO")}
                            type="date"
                            size="small"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            sx={{width: 170}}
                            slotProps={{htmlInput: {"aria-label": t("INVOICES_DATE_TO")}}}
                    />
                    <Box sx={{flex: 1}} />
                    <Typography variant="body2" sx={{color: colors.faint, pb: 1}}>
                        {t("TENANTS_SORTED_BY", {column: sortLabels[sortColumn]})} {sortDirection === "asc" ? "↑" : "↓"}
                    </Typography>
                </Box>

                <Paper variant="outlined" sx={{position: "relative", overflow: "hidden"}}>
                    {status === "loading" && (
                            <Box sx={{position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10}}>
                                <CircularProgress />
                            </Box>
                    )}
                    <TableContainer>
                        <Table aria-label={t("INVOICES_TABLE_ARIA")}>
                            <TableHead>
                                <TableRow>
                                    {sortHeader("tenant")}
                                    {sortHeader("invoiceNumber")}
                                    <TableCell>{t("INVOICES_COLUMN_BILLING")}</TableCell>
                                    <TableCell>{t("INVOICES_COLUMN_PAYMENT")}</TableCell>
                                    <TableCell>{t("INVOICES_COLUMN_EMAIL")}</TableCell>
                                    {sortHeader("createdAt")}
                                    <TableCell align="right">{t("INVOICES_COLUMN_TOTAL")}</TableCell>
                                    <TableCell sx={{width: 80}}><Box component="span" sx={{position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)"}}>{t("INVOICES_COLUMN_ACTIONS")}</Box></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAndSorted.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                                <Typography sx={{color: colors.muted}}>
                                                    {inRange.length === 0 ? t("INVOICES_EMPTY") : t("INVOICES_EMPTY_SEARCH")}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                ) : (
                                        filteredAndSorted.map((inv) => (
                                                <TableRow key={inv.id} hover>
                                                    <TableCell>
                                                        <Box sx={{fontWeight: 500, color: colors.ink}}>{inv.tenantName}</Box>
                                                        <MonoText muted>{inv.tenantId}</MonoText>
                                                    </TableCell>
                                                    <TableCell><MonoText>{inv.invoiceNumber}</MonoText></TableCell>
                                                    <TableCell>
                                                        <Box sx={{color: colors.ink}}>{inv.billingAddress.companyName}</Box>
                                                        <Box sx={{fontSize: "11.5px", color: colors.muted}}>
                                                            {inv.billingAddress.firstName} {inv.billingAddress.lastName}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {inv.status.paidAt ? (
                                                                <StatusChip tone="success" title={t("INVOICES_PAID_ON", {date: formatGermanDate(inv.status.paidAt)})}>
                                                                    {t("INVOICES_PAID_SHORT", {date: formatDayMonth(inv.status.paidAt)})}
                                                                </StatusChip>
                                                        ) : (
                                                                <StatusChip tone="warning">{t("INVOICES_NOT_PAID")}</StatusChip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {inv.status.emailSent ? (
                                                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, color: colors.muted, fontSize: "12.5px"}}>
                                                                    <MarkEmailReadIcon sx={{fontSize: 16}} aria-hidden />
                                                                    {t("INVOICES_EMAIL_SENT_SHORT", {date: formatDayMonth(inv.status.emailSent)})}
                                                                </Box>
                                                        ) : (
                                                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, color: colors.warning, fontSize: "12.5px"}}>
                                                                    <MailOutlineIcon sx={{fontSize: 16}} aria-hidden />
                                                                    {t("INVOICES_EMAIL_NOT_SENT")}
                                                                </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{formatGermanDate(inv.createdAt)}</TableCell>
                                                    <TableCell align="right" sx={{fontWeight: 500, color: colors.ink}}>{formatEuroCents(invoiceTotalInCents(inv))}</TableCell>
                                                    <TableCell sx={{py: 0, whiteSpace: "nowrap"}}>
                                                        <Tooltip title={t("INVOICES_DOWNLOAD")}>
                                                            <IconButton size="small" onClick={() => void handleDownload(inv)} aria-label={t("INVOICES_DOWNLOAD")} sx={{color: colors.body}}>
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t("INVOICES_DELETE")}>
                                                            <IconButton color="error" size="small" onClick={() => handleDeleteClick(inv)} aria-label={t("INVOICES_DELETE")}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                        ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{px: 2, py: 1.25, borderTop: `1px solid ${colors.sectionDivider}`, fontSize: "12.5px", color: colors.muted}}>
                        {t("INVOICES_SHOWING", {count: filteredAndSorted.length})}
                    </Box>
                </Paper>

                <Dialog open={deleteTarget !== null} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>{t("INVOICES_DELETE_TITLE", {number: deleteTarget?.invoiceNumber ?? ""})}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t("INVOICES_DELETE_CONFIRM", {number: deleteTarget?.invoiceNumber ?? ""})}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                            {t("CANCEL")}
                        </Button>
                        <Button color="error" variant="contained" onClick={() => void handleDeleteConfirm()} disabled={deleting}>
                            {deleting ? t("INVOICES_DELETING") : t("DELETE")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                        open={snackMessage != null}
                        autoHideDuration={6000}
                        onClose={() => setSnackMessage(null)}
                        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                >
                    <Alert severity={snackSeverity} variant="filled" onClose={() => setSnackMessage(null)}>
                        {snackMessage}
                    </Alert>
                </Snackbar>
            </>
    );
}
